import React, { useEffect, useState } from 'react';
import { Ticket } from '../types';
import { Button, Flex, Text, Card } from './base-components';
import { useTranslation } from '../i18n/TranslationContext';
import { accountLabelOf } from './transferUtils';
import { api } from '../api/client';
import { useCanDownloadTicketQr } from '../access/useFeatureAccess';

interface Props {
  ticket: Ticket;
  onTransfer: (ticket: Ticket) => void;
  onListForSale?: (ticket: Ticket) => void;
}

export const TicketCard: React.FC<Props> = ({ ticket, onTransfer, onListForSale }) => {
  const { t } = useTranslation();
  const canDownloadQr = useCanDownloadTicketQr();

  // Entry QR panel: the image is fetched with the auth header, so it is shown
  // from an object URL rather than pointing <img> at the endpoint.
  const [qrOpen, setQrOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);

  useEffect(() => {
    if (!qrOpen) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    setQrError(null);
    api.getTicketQr(ticket.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setQrUrl(objectUrl);
      })
      .catch((err: any) => {
        if (!cancelled) setQrError(err?.message || 'Could not load the QR code');
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setQrUrl(null);
    };
  }, [qrOpen, ticket.id]);

  // Extract data from the nested structure
  const metadata = ticket.ticket_metadata as any;
  const connection = ticket.external_platform_connection;
  const rawStatus = (ticket.ticket_status || ticket.status || 'active') as string;
  // Map 'removed' status to 'transferred' for display
  const status = rawStatus === 'removed' ? 'transferred' : rawStatus;
  const platform = (connection?.platform || ticket.platform || '').toUpperCase();

  // Get event info (might be just an ID or an object)
  const eventInfo = typeof ticket.event === 'object' ? ticket.event as any : null;
  const eventName = eventInfo?.name || ticket.event_name || 'Unknown Event';
  const eventDate = eventInfo?.event_date_local || eventInfo?.event_date_utc || ticket.event_date;
  const eventImage = eventInfo?.image_url;
  const eventTz = eventInfo?.timezone;

  // Get venue info
  const venueInfo = eventInfo?.venue;
  const venueName = venueInfo?.name || ticket.venue_name;
  const venueAddress = venueInfo?.address;
  const venueLocation = [venueInfo?.city, venueInfo?.state, venueInfo?.country]
    .filter(Boolean)
    .join(', ');

  // Get ticket type name
  const ticketType = metadata?.type?.name || ticket.ticket_type || 'General Admission';

  // Get cost info - API returns cents for some tickets
  const cost = metadata?.cost || metadata?.type?.cost;
  let price = cost?.amount ?? ticket.price_paid ?? 0;
  // If price > 100, assume it's in cents and convert
  if (price > 100) price = price / 100;

  // Get ticket holder
  const holder = metadata?.ticket_holder;
  const holderName = holder ? `${holder.first_name} ${holder.last_name}` : '';

  // Render a date string in venue-local time. Two input shapes are possible:
  //   (a) naive ISO ("YYYY-MM-DDTHH:mm:ss") — already venue-local. Parse the
  //       components and render with timeZone: 'UTC' so the wall clock matches
  //       the venue regardless of the browser's tz.
  //   (b) UTC ISO with Z or +/-HH:MM suffix — convert to venue tz via the
  //       event's `timezone` IANA id; if missing, render in UTC as a fallback.
  // The shape is detected by the trailing tz suffix in the string.
  const formatDate = (dateStr: string) => {
    const opts: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    };
    try {
      const isUtcInput = /(?:Z|[+-]\d{2}:?\d{2})$/.test(dateStr);
      if (isUtcInput) {
        const dt = new Date(dateStr);
        if (isNaN(dt.getTime())) return dateStr;
        return dt.toLocaleDateString('en-US', { ...opts, timeZone: eventTz || 'UTC' });
      }
      const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(dateStr);
      if (!match) return dateStr;
      const [, y, mo, d, h = '0', mi = '0', s = '0'] = match;
      const dt = new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s));
      return dt.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
    } catch {
      return dateStr;
    }
  };

  // Convert a date string into a real UTC epoch ms. Handles both shapes the
  // API uses:
  //   - UTC ISO with Z / offset → parse directly with `new Date`.
  //   - Naive venue-local ISO ("YYYY-MM-DDTHH:mm:ss") → use IANA `tz` to
  //     recover the UTC moment of that wall clock at the venue. Without `tz`
  //     we fall back to treating the components as UTC, which is at least
  //     stable across browsers.
  const venueLocalToUtcMs = (input: string, tz?: string): number | null => {
    if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(input)) {
      const ms = new Date(input).getTime();
      return isNaN(ms) ? null : ms;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(input);
    if (!match) return null;
    const [, y, mo, d, h = '0', mi = '0', s = '0'] = match;
    const asUtc = Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
    if (!tz) return asUtc;
    try {
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
      const parts = fmt.formatToParts(new Date(asUtc));
      const p = (t: string) => +(parts.find(x => x.type === t)?.value || '0');
      const hh = p('hour') === 24 ? 0 : p('hour');
      const wallAtVenue = Date.UTC(p('year'), p('month') - 1, p('day'), hh, p('minute'), p('second'));
      return asUtc - (wallAtVenue - asUtc);
    } catch {
      return asUtc;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'transferred':
        return 'bg-[#3b82f6]/20 text-[#60a5fa] border-[#3b82f6]/30';
      case 'delivery_pending':
        return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      default:
        return 'bg-white/10 text-white/50 border-white/10';
    }
  };

  // Honor the actual end time (festival end_date / explicit event_end_time).
  // Flashtools uses a 24h grace buffer — wider than the backend's 8h — so
  // tickets stay in the "active" view for a full day after the event ends.
  const endNaive =
    eventInfo?.end_date_local ||
    eventInfo?.end_date_utc ||
    eventInfo?.event_end_time_local ||
    eventInfo?.event_end_time_utc ||
    eventDate;
  const endUtcMs = endNaive ? venueLocalToUtcMs(endNaive, eventTz) : null;
  const isPastEvent = endUtcMs !== null
    ? endUtcMs + 24 * 60 * 60 * 1000 < Date.now()
    : false;

  // Always allow transfers when active + transferable; do not gate on past/expired.
  const canTransfer = status === 'active' && metadata?.is_able_to_transfer !== false;

  const showQrAction = canDownloadQr && !!ticket.qr_image_url;

  const downloadQr = () => {
    if (!qrUrl) return;
    const link = document.createElement('a');
    link.href = qrUrl;
    link.download = `ticket-${ticket.external_ticket_id || ticket.id}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get listing info
  const listing = (ticket as any).listing;
  const isListed = listing && listing.status === 'ACTIVE';
  const listingPrice = listing?.price_per_ticket ? parseFloat(listing.price_per_ticket) : null;

  return (
    <Card mb={4}>
      <Flex gap={4}>
        {/* Event Image */}
        {eventImage && (
          <div className="flex-shrink-0">
            <img
              src={eventImage}
              alt={eventName}
              className="w-24 h-24 rounded-lg object-cover"
            />
          </div>
        )}

        <Flex direction="col" grow gap={3}>
          {/* Main Info */}
          <Flex justify="between" align="start" gap={4}>
            <Flex direction="col" grow>
              <Text variant="title" mb={1}>{eventName}</Text>
              {eventDate && (
                <Text variant="muted" size="sm" mb={1}>
                  {formatDate(eventDate)}
                </Text>
              )}
              {venueName && (
                <Text variant="muted" size="sm">
                  {venueName}
                  {venueLocation && ` · ${venueLocation}`}
                </Text>
              )}
              {venueAddress && !venueLocation && (
                <Text variant="muted" size="xs" opacity={60}>
                  {venueAddress}
                </Text>
              )}
            </Flex>
            <Flex direction="col" align="end">
              <Text variant="body" mb={1}>
                {price > 0 ? `$${price.toFixed(2)}` : 'Free'}
              </Text>
              {showQrAction && !canTransfer && (
                <Button variant="secondary" size="sm" onClick={() => setQrOpen((open) => !open)}>
                  {qrOpen ? 'Hide QR' : 'QR code'}
                </Button>
              )}
              {canTransfer && (
                <Flex gap={2}>
                  {showQrAction && (
                    <Button variant="secondary" size="sm" onClick={() => setQrOpen((open) => !open)}>
                      {qrOpen ? 'Hide QR' : 'QR code'}
                    </Button>
                  )}
                  {onListForSale && !ticket.is_listed && (
                    <Button variant="secondary" size="sm" onClick={() => onListForSale(ticket)}>
                      List for sale
                    </Button>
                  )}
                  <Button variant="primary" size="sm" onClick={() => onTransfer(ticket)}>
                    {t('common.transfer')}
                  </Button>
                </Flex>
              )}
            </Flex>
          </Flex>

          {/* Tags */}
          <Flex gap={2} wrap="wrap">
            <span className="text-xs px-2.5 py-1 rounded-md bg-white/10 text-white/70 font-medium">
              {platform}
            </span>
            {isPastEvent ? (
              <span className="text-xs px-2.5 py-1 rounded-md border font-medium bg-white/5 text-white/40 border-white/10">
                past
              </span>
            ) : (
              <span
                className={`text-xs px-2.5 py-1 rounded-md border font-medium ${getStatusClass(status)}`}
                title={status === 'delivery_pending'
                  ? "The platform hasn't issued this ticket yet. It can be transferred once it has."
                  : undefined}
              >
                {status === 'delivery_pending' ? 'delivery pending' : status}
              </span>
            )}
            <span className="text-xs px-2.5 py-1 rounded-md bg-white/5 text-white/60">
              {ticketType}
            </span>
            {metadata?.simple_code && (
              <span className="text-xs px-2.5 py-1 rounded-md bg-[#3b82f6]/20 text-[#60a5fa] font-mono">
                {metadata.simple_code}
              </span>
            )}
            {isListed && (
              <span className="text-xs px-2.5 py-1 rounded-md bg-green-500/20 text-green-300 border border-green-500/30 font-medium">
                Listed ${listingPrice?.toFixed(2)}
              </span>
            )}
          </Flex>

          {/* Entry QR */}
          {showQrAction && qrOpen && (
            <div className="bg-white/5 rounded-lg p-3">
              <Flex gap={4} align="center">
                <div className="flex-shrink-0 w-32 h-32 rounded-md bg-white flex items-center justify-center">
                  {qrUrl ? (
                    <img src={qrUrl} alt={`QR code for ticket ${ticket.external_ticket_id || ticket.id}`} className="w-full h-full" />
                  ) : (
                    <span className="text-xs text-black/50">{qrError ? 'Unavailable' : 'Loading…'}</span>
                  )}
                </div>
                <Flex direction="col" gap={2}>
                  {qrError && <Text variant="muted" size="sm">{qrError}</Text>}
                  {ticket.qr_hidden && (
                    <Text variant="muted" size="sm">This ticket has already been scanned.</Text>
                  )}
                  {!ticket.qr_hidden && ticket.qr_available === false && ticket.qr_available_at && (
                    <Text variant="muted" size="sm">
                      {platform} shows this code from {formatDate(ticket.qr_available_at)}.
                    </Text>
                  )}
                  <div>
                    <Button variant="primary" size="sm" onClick={downloadQr} disabled={!qrUrl}>
                      Download
                    </Button>
                  </div>
                </Flex>
              </Flex>
            </div>
          )}

          {/* Listing Info */}
          {listing && (
            <div className="bg-white/5 rounded-lg p-3">
              <Flex justify="between" align="center">
                <Flex direction="col">
                  <Text variant="muted" size="xs" opacity={60}>Listing Status</Text>
                  <Text variant="body" size="sm">
                    {listing.status === 'ACTIVE' ? '🟢 Active' : listing.status === 'SOLD' ? '✅ Sold' : listing.status}
                  </Text>
                </Flex>
                <Flex direction="col" align="end">
                  <Text variant="muted" size="xs" opacity={60}>Asking Price</Text>
                  <Text variant="body" size="sm" color="blue">
                    ${listingPrice?.toFixed(2)} × {listing.quantity}
                  </Text>
                </Flex>
              </Flex>
            </div>
          )}

          {/* Details */}
          <div className="border-t border-white/10 pt-3">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {holderName && (
                <div>
                  <Text variant="muted" size="xs" opacity={60}>Ticket Holder</Text>
                  <Text variant="body" size="sm" mt={1}>{holderName}</Text>
                </div>
              )}
              {connection && (
                <div>
                  <Text variant="muted" size="xs" opacity={60}>Account</Text>
                  <Text variant="body" size="sm" mt={1}>{accountLabelOf(ticket)}</Text>
                </div>
              )}
              <div>
                <Text variant="muted" size="xs" opacity={60}>Ticket ID</Text>
                <Text variant="body" size="sm" mt={1}>{ticket.external_ticket_id || metadata?.id}</Text>
              </div>
            </div>
          </div>
        </Flex>
      </Flex>
    </Card>
  );
};


